import { useRef, useState } from 'react';
import { useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { AdminHub, AdminHubDetail, IssuedClaimCode } from '../../../domain/Hub';

// El hub manda su salud cada minuto: se refresca para verla en vivo.
const REFRESH_MS = 30000;

export function useAdminHubs() {
  const { admin } = useRepositories();
  const [hubs, setHubs] = useState<AdminHub[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useIonViewWillEnter(() => {
    admin
      .fetchHubs()
      .then(({ hubs }) => setHubs(hubs))
      .catch((err) => setError(err instanceof Error ? err.message : 'No se pudieron cargar los hubs.'))
      .finally(() => setLoading(false));
  });

  return { hubs, loading, error };
}

export function useAdminHubDetail(hubId: string) {
  const { admin } = useRepositories();
  const [data, setData] = useState<AdminHubDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  // El código nuevo se ve una sola vez: para reimprimir la etiqueta.
  const [issued, setIssued] = useState<IssuedClaimCode | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    try {
      setData(await admin.fetchHub(hubId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el hub.');
    } finally {
      setLoading(false);
    }
  };

  useIonViewWillEnter(() => {
    load();
    timer.current = setInterval(load, REFRESH_MS);
  });
  useIonViewWillLeave(() => {
    if (timer.current) clearInterval(timer.current);
  });

  const assign = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { createdStations } = await admin.assignHub(hubId, email.trim());
      setToast(`Hub asignado a ${email.trim()}${createdStations.length ? ` (${createdStations.length} estaciones nuevas)` : ''}.`);
      setEmail('');
      await load();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'No se pudo asignar el hub.');
    } finally {
      setBusy(false);
    }
  };

  const regenerateCode = async () => {
    setBusy(true);
    try {
      setIssued(await admin.regenerateHubCode(hubId));
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'No se pudo generar el código.');
    } finally {
      setBusy(false);
    }
  };

  const setBrokerAccess = async (enabled: boolean) => {
    setBusy(true);
    try {
      await admin.setHubBrokerAccess(hubId, enabled);
      setToast(enabled ? 'Hub desbloqueado: puede volver a conectarse.' : 'Hub bloqueado: el broker lo desconectó.');
      await load();
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'No se pudo cambiar el acceso al broker.');
    } finally {
      setBusy(false);
    }
  };

  return { data, loading, error, toast, setToast, email, setEmail, busy, assign, issued, regenerateCode, setBrokerAccess };
}
